import { WhmStack } from "@securitize/reactjs-whm";
import PortfolioValueCard from "./PortfolioValueCard";
import CashBalanceCard from "./CashBalanceCard";
import { createContext, useContext } from "react";
import {
  useGetCashAccount,
  useGetPortfolioBalances,
  useGetPortfolioPerformance,
} from "../../../data/usePortfolio";

// Context to share loading states between container and cards
interface PortfolioTopSectionLoadingContextType {
  isContainerLoading: boolean;
  cashAccountQuery: ReturnType<typeof useGetCashAccount>;
  portfolioBalancesQuery: ReturnType<typeof useGetPortfolioBalances>;
  portfolioPerformanceQuery: ReturnType<typeof useGetPortfolioPerformance>;
}

const PortfolioTopSectionLoadingContext =
  createContext<PortfolioTopSectionLoadingContextType | null>(null);

export const usePortfolioTopSectionLoadingContext = () => {
  const context = useContext(PortfolioTopSectionLoadingContext);
  if (!context) {
    throw new Error(
      "usePortfolioTopSectionLoadingContext must be used within PortfolioTopSectionContainer",
    );
  }
  return context;
};

export default function PortfolioTopSectionContainer() {
  // Make all API calls at the container level for synchronized loading
  const cashAccountQuery = useGetCashAccount();
  const portfolioBalancesQuery = useGetPortfolioBalances();
  const portfolioPerformanceQuery = useGetPortfolioPerformance();

  const isContainerLoading =
    cashAccountQuery.isLoading ||
    portfolioBalancesQuery.isLoading ||
    portfolioPerformanceQuery.isLoading;

  const contextValue: PortfolioTopSectionLoadingContextType = {
    isContainerLoading,
    cashAccountQuery,
    portfolioBalancesQuery,
    portfolioPerformanceQuery,
  };

  // Determine if cash account should be shown based on the response
  const shouldShowCashAccount =
    !!cashAccountQuery.data?.shouldShowCashAccount || isContainerLoading;

  return (
    <PortfolioTopSectionLoadingContext.Provider value={contextValue}>
      <WhmStack
        direction={{ xs: "column", xl: "row" }}
        sx={{
          gap: "16px",
          alignItems: "stretch",
        }}
      >
        <PortfolioValueCard sx={{ flex: 1 }} />
        {shouldShowCashAccount && <CashBalanceCard sx={{ flex: 1 }} />}
      </WhmStack>
    </PortfolioTopSectionLoadingContext.Provider>
  );
}
